import React from 'react';
import { useIntl, defineMessages } from 'react-intl';
import clsx from 'clsx';
import { TypedMemo } from '../../../../common/types';
import { BillingInterval, yearlyToMonthly } from '../../../config/music24StripePlans';
import { formatPlanPrice, useDisplayCurrency } from '../../../config/useDisplayCurrency';
import { PlanHighlight } from '../../../plans/planHighlights';
import { PlanHighlightList } from '../../../plans/PlanHighlightList';
import { teamOwnerMessages } from '../../../../team/teamOwnerMessages';
import { PricingButton } from './PricingButton';
import { pricingCardMessages } from './pricingCardMessages';
import styles from './PricingPlanCard.module.scss';
import { withBrandWord } from '../../primitives/BrandWord';

const messages = defineMessages({
  mostPopular: { id: 'app.sml.pricing.badge.mostPopular', defaultMessage: 'Most popular' },
  selectedForYou: { id: 'app.sml.pricing.badge.selected', defaultMessage: 'Selected for you' },
  perMonth: { id: 'app.sml.pricing.period.perMonth', defaultMessage: '/ month' },
  billedYearly: { id: 'app.sml.pricing.billedYearly', defaultMessage: 'Billed {amount} a year · ' },
  twoMonthsFree: { id: 'app.sml.pricing.toggle.twoMonthsFree', defaultMessage: '2 months free' },
  billedMonthly: { id: 'app.sml.pricing.billedMonthly', defaultMessage: 'Billed monthly · cancel anytime' },
  loading: { id: 'app.sml.pricing.button.loading', defaultMessage: 'Redirecting...' },
});

export interface PricingPlanCardProps {
  /** Already localized — the parent resolves these through static `defineMessages` so the ids
   * (which come from data, not a literal in a `formatMessage()` call) stay extractable. */
  name: string;
  description: string;
  /** "Everything in Plus, plus": the lead of the short list. */
  highlightsLead: string;
  /** The rows that differ from the plan this one builds on (`planHighlights`). */
  highlights: PlanHighlight[];
  interval: BillingInterval;
  monthlyAmountCents: number;
  yearlyAmountCents: number;
  mostPopular: boolean;
  /** The plan a website "Choose <plan>" button named (ruling C2) — gets the same featured
   * treatment as `mostPopular`, plus its own badge when it isn't already the popular card. */
  highlighted: boolean;
  isCurrent: boolean;
  /** The signed-in user's current plan is the reverse trial, at Pro level. */
  isTrialing: boolean;
  /** `null` renders no button at all (inside a native app's web view). */
  ctaLabel: string | null;
  isLoading: boolean;
  disabled: boolean;
  /** Design o5: a team member sees the dashed "Owner only" slot on every non-current card
   * instead of a button — takes priority over `ctaLabel`. */
  ownerOnly: boolean;
  /** Inside a native app's web view (App Store 3.1.1, Play billing): no web price, no billing line. */
  hidePrice?: boolean;
  onSubscribe: () => void;
}

/**
 * One paid plan of the plans page (design plans-page a): price, one button, the short list. The
 * price is for one artist; the card has no artist stepper (R8, owner 2026-10-23).
 */
const PricingPlanCardComponent: React.FC<PricingPlanCardProps> = ({
  name,
  description,
  highlightsLead,
  highlights,
  interval,
  monthlyAmountCents,
  yearlyAmountCents,
  mostPopular,
  highlighted,
  isCurrent,
  isTrialing,
  ctaLabel,
  isLoading,
  disabled,
  ownerOnly,
  hidePrice = false,
  onSubscribe,
}) => {
  const intl = useIntl();
  const currency = useDisplayCurrency();
  const perMonthCents = interval === 'yearly' ? yearlyToMonthly(yearlyAmountCents) : monthlyAmountCents;
  const isFeatured = (mostPopular || highlighted) && !isCurrent;
  const showPopularBadge = mostPopular && !isCurrent;
  const showSelectedBadge = highlighted && !mostPopular && !isCurrent;

  return (
    <div className={clsx(styles.plan, isFeatured && styles.featPlan, isCurrent && styles.currentPlan)}>
      {showPopularBadge && <div className={styles.badge}>{intl.formatMessage(messages.mostPopular)}</div>}
      {showSelectedBadge && <div className={styles.badge}>{intl.formatMessage(messages.selectedForYou)}</div>}

      <div className={styles.head}>
        <div className={styles.pName}>
          <span>{name}</span>
          {isCurrent && (
            <span className={styles.currentChip}>
              {intl.formatMessage(isTrialing ? pricingCardMessages.currentPlanTrial : pricingCardMessages.currentPlan)}
            </span>
          )}
        </div>
        <p className={styles.pDesc}>{withBrandWord(description)}</p>
      </div>

      {!hidePrice && (
        <div className={styles.price}>
          <div className={styles.pPrice}>
            <span className={styles.amt}>{formatPlanPrice(intl, perMonthCents, currency)}</span>
            <span className={styles.per}>{intl.formatMessage(messages.perMonth)}</span>
          </div>
          <div className={styles.pBilled}>
            {interval === 'yearly' ? (
              <>
                {intl.formatMessage(messages.billedYearly, {
                  amount: formatPlanPrice(intl, yearlyAmountCents, currency),
                })}
                <b>{intl.formatMessage(messages.twoMonthsFree)}</b>
              </>
            ) : (
              intl.formatMessage(messages.billedMonthly)
            )}
          </div>
        </div>
      )}

      {ownerOnly ? (
        <div className={styles.ownerOnly}>{intl.formatMessage(teamOwnerMessages.ownerOnly)}</div>
      ) : (
        ctaLabel !== null && (
          <PricingButton
            variant={isFeatured ? 'primary' : 'ghost'}
            className={styles.pCta}
            disabled={disabled || isCurrent}
            onClick={onSubscribe}
          >
            {isLoading ? intl.formatMessage(messages.loading) : ctaLabel}
          </PricingButton>
        )
      )}

      <PlanHighlightList className={styles.highlights} lead={highlightsLead} highlights={highlights} />
    </div>
  );
};

export const PricingPlanCard = TypedMemo(PricingPlanCardComponent);
